import type { FormField } from "@/lib/database/forms";

/**
 * Overlays a saved response's answers on top of freshly-initialized default
 * form data (saved answers always win), then reconciles "ranking" fields'
 * saved order against the field's CURRENT choices — dropping any choice
 * removed since the response was saved and appending any choice added since,
 * so the ranking control never renders an option it can't find or silently
 * drops a newly-added one.
 *
 * Shared between the standalone form page's edit-prefill and the landing-page
 * modal's edit-prefill — both need the identical merge/reconcile behavior.
 *
 * @param formFields - The form's current field definitions.
 * @param initialData - Freshly-initialized (and viewer-prefilled) default form data.
 * @param savedAnswers - The saved response's answers to overlay.
 * @returns The merged form data, ready to render into the form.
 */
export function mergeSavedResponseOverDefaults(
  formFields: FormField[],
  initialData: Record<string, unknown>,
  savedAnswers: Record<string, unknown>
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...initialData, ...savedAnswers };
  formFields.forEach((field) => {
    if (field.type !== "ranking") return;
    const savedRanking = Array.isArray(merged[field.label]) ? (merged[field.label] as string[]) : [];
    const currentChoices = field.choices || [];
    const validSaved = savedRanking.filter((item) => currentChoices.includes(item));
    const newChoices = currentChoices.filter((choice) => !savedRanking.includes(choice));
    merged[field.label] = [...validSaved, ...newChoices];
  });
  return merged;
}
